/**
 * The operator's busiest zone, by name — for the Avery launchpad chips.
 *
 * The design demo's chips name real lots ("Kettle Row"), which is most of why
 * they teach: a chip reading "Raise weekend evening rates at Kettle Row" shows
 * an ask worth making, where "…at my busiest zone" shows a template. Production
 * cannot hardcode a name, so it resolves one.
 *
 * ## Which "busiest"
 *
 * `/api/dashboard/summary` carries two candidates and they are not equally
 * usable:
 *
 *   - `week.topZones[0]` — 7 days of durable-ledger revenue, and it already
 *     carries `zoneName`. STABLE, which is the whole requirement: a teaching
 *     chip that renames itself through the day teaches that the console is
 *     twitchy.
 *   - `insights.busiestZone` — TODAY only, in each zone's local calendar day
 *     (dashboard/index.ts computeTodayMetrics). Null before the day's first
 *     session and thin for hours after, so at 8am it would name whichever lot
 *     happened to take two cars. Used only as a fallback, when the week block
 *     is absent (`week` is null wherever D1 is unbound — the summary route
 *     degrades to today-only by design).
 *
 * Its `zoneId` field is already a DISPLAY NAME server-side (zoneDisplayName,
 * falling back to the raw id when the zone record is missing), so neither
 * candidate needs a second lookup to render.
 *
 * ## Why the caller passes `enabled` rather than this hook checking a perm
 *
 * "Which of your zones earns the most" is a revenue disclosure even with no
 * figure attached, so it belongs to `view.analytics` — the permission
 * TOOL_PERMISSION gives `analyze_zone_revenue`, the tool that answers this exact
 * question. Keeping the check at the call site means the ONE place that decides
 * is next to the chips it affects, rather than split across two files. The
 * endpoint enforces `view.transactions` independently; a 403 from either
 * direction lands on the same `null`.
 *
 * Never throws and never blocks a render: the launchpad paints immediately with
 * the generic wording and upgrades in place if a name arrives. Every failure —
 * 403, D1 unbound, offline, a tenant with no sessions yet — is `null`, which
 * the chips read as "keep the generic phrasing".
 */
import { useCallback } from 'react'
import { useAsyncRead } from './useAsyncRead'
import { useAuth } from '../auth/AuthContext'
import type { DashboardSummaryResponse } from '@parkify/api-types'
import { callApi } from '../config/apiBase'

/** Display name of the busiest zone, or null when unknown/not permitted. */
export function useBusiestZoneName(enabled: boolean): string | null {
  const { authHeaders } = useAuth()
  const load = useCallback(async () => {
    const res = await callApi(`/api/dashboard/summary`, {
      headers: authHeaders() as Record<string, string>,
    })
    if (!res.ok) return null               // 403 included — stay generic
    const d = await res.json() as DashboardSummaryResponse
    const weekly = d.week?.topZones?.[0]?.zoneName?.trim()
    const today = d.insights?.busiestZone?.zoneId?.trim()
    const resolved = weekly || today || null
    // A name long enough to swamp the chip's own sentence reads worse than
    // the template it replaced, so leave those alone.
    return resolved && resolved.length <= 40 ? resolved : null
  }, [authHeaders])
  // Keyed on the headers' CONTENT, not the function's identity: a view-as
  // switch (a different tenant's busiest zone) refetches, a re-created but
  // equivalent authHeaders does not blank the chip.
  const state = useAsyncRead(enabled ? JSON.stringify(authHeaders()) : null, load)
  return state.status === 'success' ? state.data : null
}
